import React, { useState, useEffect } from "react";
import { Col, Modal, ModalBody, ModalFooter, ModalHeader, Label, Input, Row } from "reactstrap";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";

import {
  listListedProductsByParams,
  getListedProductById,
  verifyListedProduct,
  deleteListedProduct
} from "../../api/listedProducts.api";

import StatusBadge from "../../components/StatusBadge";

const AdminListedProducts = () => {
  const [data, setData] = useState([]);
  const [values, setValues] = useState({});
  const [query, setQuery] = useState("");
  const [modal_view, setModalView] = useState(false);
  const [modal_delete, setModalDelete] = useState(false);
  const [_id, set_Id] = useState("");
  const [remove_id, setRemove_id] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [totalRows, setTotalRows] = useState(0);

  // --- DATA FETCHING ---
  const fetchData = async () => {
    const skip = (pageNo - 1) * perPage;
    const res = await listListedProductsByParams({ skip, per_page: perPage, match: query });
    if (res.data.data.length > 0) {
      const result = res.data.data[0];
      setData(result.data);
      setTotalRows(result.count);
    } else {
      setData([]);
    }
  };

  useEffect(() => { fetchData(); }, [query, pageNo, perPage]);

  // --- HANDLERS ---
  const handleView = async (id) => {
    set_Id(id);
    const res = await getListedProductById(id);
    setValues(res.data.data);
    setModalView(true);
  };

  const handleVerify = async (id, status) => {
    await verifyListedProduct(id, { is_verified: status });
    toast.success("Verification Updated");
    fetchData();
  };

  const handleDelete = async () => {
    await deleteListedProduct(remove_id);
    toast.success("Deleted");
    setModalDelete(false);
    fetchData();
  };

  // --- CUSTOM TABLE STYLES (Matching ProductMaster) ---
  const customStyles = {
    headRow: { style: { borderBottom: "2px solid #f3f4f6" } },
    headCells: {
      style: {
        color: "#6b7280",
        fontWeight: "700",
        textTransform: "uppercase",
        fontSize: "11px",
        letterSpacing: "0.5px"
      }
    },
    cells: { style: { fontSize: "14px", color: "#374151", padding: "15px 10px" } },
  };

  const columns = [
    { name: "Sr No", selector: (r, i) => i + 1, width: "70px" },
    { name: "Seller", selector: r => r.business_name, grow: 1, style: { fontWeight: "600", color: "#112347" } },
    { name: "Product", selector: r => r.product_name, grow: 1.5, style: { fontWeight: "600" } },
    {
        name: "Brand / Model",
        cell: r => (
          <div>
            <div className="fw-bold">{r.brand_name}</div>
            <div className="text-muted small">{r.model_number}</div>
          </div>
        )
      },
    { name: "MOQ", selector: r => r.min_orders, center: true },
    {
      name: "Status",
      center: true,
      cell: (r) => (
        <StatusBadge status={r.is_verified} />
      )
    },
    {
      name: "Actions",
      right: true,
      cell: (row) => (
        <div className="d-flex gap-3">
          <button className="btn btn-link p-0 text-muted" onClick={() => handleView(row.id)}>👁️</button>
          {row.is_verified !== "VERIFIED" ? (
            <button className="btn btn-link p-0 text-success" onClick={() => handleVerify(row.id, 1)}>✅</button>
          ) : (
            <button className="btn btn-link p-0 text-warning" onClick={() => handleVerify(row.id, 0)}>🚫</button>
          )}
          <button className="btn btn-link p-0 text-danger" onClick={() => { setRemove_id(row.id); setModalDelete(true) }}>🗑️</button>
        </div>
      )
    }
  ];

  return (
    <div style={{ backgroundColor: "#ffffff", padding: "clamp(15px, 3vw, 24px)", borderRadius: "12px", border: "1px solid #e5e7eb" }}>
      
      {/* 1. HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold m-0" style={{ color: "#112347" }}>Seller Product Listings</h4>
          <p className="text-muted small m-0">Review and verify products listed by marketplace sellers</p>
        </div>
      </div>

      {/* 2. SEARCH BAR */}
      <Row className="mb-4">
        <Col md="4" className="position-relative">
          <span className="position-absolute translate-middle-y top-50 ms-3 text-muted">🔍</span>
          <Input
            placeholder="Search Listings (Seller, Product, Model)..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "8px", paddingLeft: "40px" }}
          />
        </Col>
      </Row>

      {/* 3. TABLE */}
      <div className="table-responsive border rounded-3 overflow-hidden">
        <DataTable
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangePage={setPageNo}
          onChangeRowsPerPage={setPerPage}
          customStyles={customStyles}
        />
      </div>

      {/* 4. VIEW MODAL (Styled Review Overlay) */}
      <Modal isOpen={modal_view} toggle={() => setModalView(false)} centered style={{ maxWidth: '700px' }}>
        <ModalHeader className="border-0 pb-0 pt-4 px-4" toggle={() => setModalView(false)}>
          <div className="d-flex align-items-center gap-2">
            <h4 className="fw-bold m-0" style={{ color: "#112347" }}>{values.product_name}</h4>
            <span style={{ 
              backgroundColor: values.is_verified ? "#ecfdf5" : "#fffbeb", 
              color: values.is_verified ? "#059669" : "#d97706", 
              padding: "2px 12px", borderRadius: "20px", fontSize: "11px", fontWeight: "700"
            }}>
              {values.is_verified === "VERIFIED" ? "Verified" : "Verification Required"}
            </span>
          </div>
          <p className="text-muted small m-0 mt-1">Review complete listing details from {values.business_name}</p>
        </ModalHeader>

        <ModalBody className="px-4" style={{ height: '450px', overflowY: 'auto' }}>
          <hr className="my-3 opacity-25" />
          
          <Row className="g-4">
             <Col md="6">
                <label className="text-muted small d-block mb-1 text-uppercase fw-bold">Seller Information</label>
                <div className="fw-bold text-dark">🏢 {values.business_name}</div>
             </Col>
             <Col md="6">
                <label className="text-muted small d-block mb-1 text-uppercase fw-bold">Specifications</label>
                  <div className="fw-bold text-dark">Brand: {values.brand_name}</div>
                  <div className="text-muted small">Model: {values.model_number}</div>
             </Col>
             <Col md="6">
                <label className="text-muted small d-block mb-1 text-uppercase fw-bold">Order Details</label>
                <div className="fw-bold">MOQ: {values.min_orders} Units</div>
                <div className="text-muted small">Warranty: {values.warranty_years} Years</div>
             </Col>
          </Row>

          <hr className="my-4 opacity-25" />

          <div>
             <label className="text-muted small d-block mb-2 text-uppercase fw-bold">Product Description</label>
             <p className="text-muted" style={{ lineHeight: '1.6', fontSize: '14px' }}>
                {values.description || "No description provided for this listing."}
             </p>
          </div>
        </ModalBody>

        <ModalFooter className="border-0 px-4 pb-4 pt-3 bg-white">
           <div className="d-flex gap-2 w-100">
              <button 
                className={`btn flex-grow-1 fw-bold ${values.is_verified ? 'btn-outline-warning' : 'btn-success'}`}
                onClick={() => { handleVerify(values.id, values.is_verified ? 0 : 1); setModalView(false); }}
              >
                {values.is_verified ? "🚫 Mark as Unverified" : "✅ Approve & Verify Listing"}
              </button>
           </div>
        </ModalFooter>
      </Modal>

      {/* 5. DELETE MODAL */}
      <Modal isOpen={modal_delete} toggle={() => setModalDelete(false)} centered>
        <ModalBody className="text-center py-4">
          <h5 className="fw-bold">Confirm Deletion</h5>
          <p className="text-muted">Are you sure you want to permanently remove this seller's product listing?</p>
          <div className="d-flex gap-2 justify-content-center mt-4">
            <button className="btn btn-light px-4" onClick={() => setModalDelete(false)}>Cancel</button>
            <button className="btn btn-danger px-4 fw-bold" onClick={handleDelete}>Delete Listing</button>
          </div>
        </ModalBody>
      </Modal>

    </div>
  );
};

export default AdminListedProducts;